import { sessionHintCookie, type SessionHint } from '@goodbuild/rules/session-hint';
import { displayNameCookie } from '@goodbuild/rules/display-name';

/**
 * Telling the storefront who is asking, as far as a header needs to know.
 *
 * This app is the only surface that knows: the session is a bearer token in
 * this origin's `localStorage`, and the store is a different origin that cannot
 * read it. So the answer is written where both can see it — a cookie on the
 * registrable domain carrying the character `1`, and since #62 up to three
 * letters after it for the role links the bar may draw.
 *
 * `@goodbuild/rules/session-hint` holds the contract and explains at length
 * why a hint is not the shared session cookie `routes/game.tsx` refuses, and why
 * the roles did not change that. The only thing that belongs here is the
 * assignment, so that the decision about what the cookie says stays testable
 * and this stays one line of browser.
 *
 * `true` rather than a standing means *signed in, roles unknown* and `false`
 * means clear it. Both are real states here: `endSession` has nothing left to
 * describe, and a session that `/me` has not answered for yet knows the first
 * half of the answer and not the second.
 */
export function rememberSession(hint: boolean | SessionHint): void {
  // Guarded rather than assumed: this app is a plain SPA today, but a `document`
  // that does not exist is a blank page rather than an error worth risking.
  if (typeof document === 'undefined') return;
  document.cookie = sessionHintCookie(hint, window.location);
}

/**
 * Telling the storefront what to call this visitor.
 *
 * **A second cookie beside the hint, and it stays a second cookie.** The name
 * could have been a fifth field in the hint and must not be: the whole reason
 * the store may act on `gb_signed_in` without acquiring an identity layer is
 * that the value names nobody, and one field would end that argument.
 * `@goodbuild/rules/display-name` holds the contract and says all of it at
 * length — including why nothing on either origin may believe what this says.
 *
 * **Written and cleared in the same breath as the hint**, which is the one rule
 * this function exists to make easy to keep. They travel together, they expire
 * together, and they are scoped identically because the attributes come from
 * the hint's own module rather than being restated. A name that outlived the
 * hint would be a store header greeting somebody by the previous visitor's
 * name; the storefront also refuses to read this unless the hint says somebody
 * is signed in, so that state is unreachable rather than merely unlikely.
 *
 * `null` clears it, and is what a sign-out and an account with no name both
 * pass — there is nothing to say in either case, and the bar falls back to the
 * word "Account".
 */
export function rememberName(name: string | null): void {
  // The same guard, for the same reason as above.
  if (typeof document === 'undefined') return;
  document.cookie = displayNameCookie(name, window.location);
}
